import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, useNavigate, createSearchParams } from 'react-router-dom';
import {
    MapPin, User, Bed, Bath, Star,
    Calendar as CalendarIcon, ChevronRight, CheckCircle, AlertCircle,
    X, ChevronLeft, Loader2, Tag, ArrowLeft, DoorOpen, ChevronDown, ChevronUp, MessageSquare, Info
} from 'lucide-react';
import { getProperty, getQuote, getReviews, getCalendar } from '../services/apiService';
import { Property, QuoteResponse, Review } from '../types';
import BookingPaymentModal from './BookingPaymentModal';
import { Circle, MapContainer, TileLayer, useMap } from "react-leaflet";

// --- HELPER DATE FUNCTIONS ---
const formatDate = (date: Date) => {
    const d = new Date(date);
    const month = '' + (d.getMonth() + 1);
    const day = '' + d.getDate();
    const year = d.getFullYear();
    return [year, month.padStart(2, '0'), day.padStart(2, '0')].join('-');
};

const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

interface SectionItemProps {
    title: string;
    content: string;
    initialOpen?: boolean;
}

const DescriptionSectionItem = ({ title, content, initialOpen = false }: SectionItemProps) => {
    const [isOpen, setIsOpen] = useState(initialOpen);

    return (
        <div className="border-b border-gray-200 last:border-0">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full py-4 text-left group focus:outline-none"
            >
                <h3 className={`font-bold text-lg transition-colors ${isOpen ? 'text-brand' : 'text-gray-900 group-hover:text-brand'}`}>
                    {title}
                </h3>
                {isOpen ? (
                    <ChevronUp className="w-5 h-5 text-brand" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 group-hover:text-brand" />
                )}
            </button>
            {isOpen && (
                <div className="pb-4 text-gray-600 leading-relaxed whitespace-pre-line animate-fade-in">
                    {content}
                </div>
            )}
        </div>
    );
};

// --- COMPONENTE PER IL DETTAGLIO PREZZI (DRY: Don't Repeat Yourself) ---
const PriceBreakdown = ({ quote, coupon }: { quote: QuoteResponse, coupon: string }) => {

    // LOGICA AVANZATA PER ESTRARRE I DATI RAW
    const rawData = (quote as any).DEBUG_RAW;
    const invoiceItems = rawData?.rates?.ratePlans?.[0]?.ratePlan?.money?.invoiceItems || [];

    // 1. Identifichiamo TUTTI gli elementi che sono sconti (Importo negativo o tipo specifico)
    // Cerchiamo sia le PROMOTION che i DISCOUNT
    const discountItems = invoiceItems.filter((item: any) =>
        (item.type === 'DISCOUNT' || item.type === 'PROMOTION') && item.amount < 0
    );

    // Calcoliamo il totale assoluto di tutti gli sconti per far tornare i conti matematici
    const totalDiscountVal = discountItems.reduce((acc: number, item: any) => acc + Math.abs(item.amount), 0);

    // 2. Calcoliamo eventuali "Altre Fees" (es. Damage Coverage) per far tornare i conti
    // Formula: Altro = TotaleReale - (Accomodation + Cleaning + Tasse - ScontiTotali)
    const accommodation = quote.accommodationPrice || 0;
    const cleaning = quote.cleaningFee || 0;
    const taxes = quote.taxes || 0;
    const total = quote.totalPrice || 0;

    const knownTotal = accommodation + cleaning + taxes - totalDiscountVal;
    let otherFeesVal = total - knownTotal;

    // Arrotondamento a 2 decimali per evitare problemi di virgola mobile (es. 79.99999)
    otherFeesVal = Math.round(otherFeesVal * 100) / 100;

    // Se otherFeesVal è molto piccolo (errore di arrotondamento), consideralo 0
    if (Math.abs(otherFeesVal) < 0.05) otherFeesVal = 0;

    return (
        <div className="bg-gray-50 p-5 rounded-2xl space-y-3 text-sm border border-gray-100 animate-fade-in">
            {/* COSTO AFFITTO */}
            <div className="flex justify-between text-gray-600">
                <span>{quote.currency} {quote.nightlyPrice?.toFixed(2)} x {quote.nights} nights</span>
                <span>{quote.currency} {quote.accommodationPrice?.toFixed(2)}</span>
            </div>

            {/* CLEANING FEE */}
            <div className="flex justify-between text-gray-600">
                <span>Cleaning Fee</span>
                <span>{quote.currency} {quote.cleaningFee?.toFixed(2)}</span>
            </div>

            {/* ALTRE FEES (Damage Coverage, Service Fee, etc.) */}
            {otherFeesVal > 0 && (
                <div className="flex justify-between text-gray-600">
                    <span>Service & Damage Fees</span>
                    <span>{quote.currency} {otherFeesVal.toFixed(2)}</span>
                </div>
            )}

            {/* TASSE */}
            <div className="flex justify-between text-gray-600">
                <span>Taxes / Booking Fees</span>
                <span>{quote.currency} {quote.taxes?.toFixed(2)}</span>
            </div>

            {/* SCONTI E PROMOZIONI - LISTA DINAMICA */}
            {discountItems.length > 0 && (
                <div className="border-t border-dashed border-green-200 pt-2 mt-2 space-y-1">
                    {discountItems.map((item: any, index: number) => (
                        <div key={index} className="flex justify-between text-green-600 font-bold bg-green-50 p-2 rounded-lg -mx-2">
                            <span className="flex items-center gap-1">
                                <Tag className="w-3 h-3"/>
                                {/* Usiamo il titolo restituito da Guesty (es. 'Off Season Coupon' o 'GPO #...') */}
                                {item.type === 'DISCOUNT' && coupon && item.description?.toUpperCase() === coupon.toUpperCase()
                                    ? `Coupon (${coupon})` // Se corrisponde al codice inserito dall'utente
                                    : 'Special Offer'
                                }
                            </span>
                            <span>- {quote.currency} {Math.abs(item.amount).toFixed(2)}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* TOTALE */}
            <div className="border-t border-gray-200 pt-3 flex justify-between font-bold text-lg text-gray-900">
                <span>Total</span>
                <span>{quote.currency} {quote.totalPrice?.toFixed(2)}</span>
            </div>
        </div>
    );
};

const PropertyDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [urlParams] = useSearchParams();
    const navigate = useNavigate();

    const bookingSectionRef = useRef<HTMLDivElement>(null);

    // --- STATE ---
    const [property, setProperty] = useState<Property | null>(null);
    const [loading, setLoading] = useState(true);
    const [reviews, setReviews] = useState<Review[]>([]);

    // Booking State
    const [checkIn, setCheckIn] = useState(urlParams.get('checkIn') || '');
    const [checkOut, setCheckOut] = useState(urlParams.get('checkOut') || '');
    const [guests, setGuests] = useState(Number(urlParams.get('guests')) || 1);
    const [coupon, setCoupon] = useState('');

    // Calendar State
    const [calendarData, setCalendarData] = useState<Record<string, { status: string, price?: number, minNights?: number }>>({});
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [calendarViewDate, setCalendarViewDate] = useState(new Date());
    const [calendarLoading, setCalendarLoading] = useState(false);
    const [selectingField, setSelectingField] = useState<'checkIn' | 'checkOut'>('checkIn');

    // Quote State
    const [quote, setQuote] = useState<QuoteResponse | null>(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Modals & UI State
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [isAmenitiesModalOpen, setIsAmenitiesModalOpen] = useState(false);
    const [isReviewsModalOpen, setIsReviewsModalOpen] = useState(false);

    // Description Expand State
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const MapUpdater = ({ center }: { center: [number, number] }) => {
        const map = useMap();
        map.setView(center, 14);
        return null;
    };

    // --- INITIAL FETCH ---
    useEffect(() => {
        const fetchData = async () => {
            try {
                if (id) {
                    setLoading(true);
                    const propertyData = await getProperty(id);
                    setProperty(propertyData);

                    if (propertyData.reviews && propertyData.reviews.length > 0) {
                        setReviews(propertyData.reviews);
                    } else {
                        const reviewsData = await getReviews(id);
                        setReviews(reviewsData);
                    }
                }
            } catch (error) {
                console.error("Error fetching data", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id]);

    // --- AUTO-CALCULATE QUOTE ---
    useEffect(() => {
        if (property && id && checkIn && checkOut) {
            handleCalculateQuote();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [property]);

    // --- KEYBOARD HANDLING ---
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (isGalleryOpen) closeGallery();
                if (isAmenitiesModalOpen) toggleAmenitiesModal(false);
                if (isReviewsModalOpen) toggleReviewsModal(false);
                if (isCalendarOpen) setIsCalendarOpen(false);
                if (isBookingModalOpen) setIsBookingModalOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isGalleryOpen, isAmenitiesModalOpen, isCalendarOpen, isBookingModalOpen, isReviewsModalOpen]);

    // --- CALENDAR LOGIC ---
    const fetchCalendarData = async (startDate: Date, monthsToLoad: number = 2) => {
        if (!id) return;
        setCalendarLoading(true);
        const startStr = formatDate(startDate);
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + monthsToLoad);
        const endStr = formatDate(endDate);
        try {
            const response = await getCalendar(id, startStr, endStr);
            const daysList = Array.isArray(response) ? response : (response.data || []);
            const newData: Record<string, any> = {};
            if (Array.isArray(daysList)) {
                daysList.forEach((day: any) => {
                    newData[day.date] = { status: day.status, price: day.price, minNights: day.minNights };
                });
                setCalendarData(prev => ({ ...prev, ...newData }));
            }
        } catch (e) { console.error("Calendar error", e); }
        finally { setCalendarLoading(false); }
    };

    const openCalendarModal = (field: 'checkIn' | 'checkOut') => {
        setSelectingField(field);
        setIsCalendarOpen(true);
        const todayStr = formatDate(new Date());
        if (!calendarData[todayStr]) fetchCalendarData(new Date(), 3);
    };

    const handleMonthChange = (increment: number) => {
        const newDate = new Date(calendarViewDate);
        newDate.setMonth(newDate.getMonth() + increment);
        setCalendarViewDate(newDate);
        fetchCalendarData(newDate, 2);
    };

    const handleDateSelect = (dateStr: string, isAvailable: boolean) => {
        if (!isAvailable) return;
        if (selectingField === 'checkIn') {
            setCheckIn(dateStr);
            setCheckOut('');
            setSelectingField('checkOut');
        } else {
            if (dateStr < checkIn) {
                setCheckIn(dateStr);
                setCheckOut('');
            } else if (dateStr !== checkIn) {
                setCheckOut(dateStr);
                setIsCalendarOpen(false);
            }
        }
    };

    const renderCalendarGrid = () => {
        const year = calendarViewDate.getFullYear();
        const month = calendarViewDate.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const days = [];
        for (let i = 0; i < firstDay; i++) { days.push(<div key={`empty-${i}`} className="h-10 md:h-12"></div>); }
        const today = new Date(); today.setHours(0, 0, 0, 0);

        for (let d = 1; d <= daysInMonth; d++) {
            const currentDate = new Date(year, month, d);
            const dateStr = formatDate(currentDate);
            const data = calendarData[dateStr];
            const isPast = currentDate < today;
            const isAvailable = !isPast && data?.status === 'available';
            const isSelected = dateStr === checkIn || dateStr === checkOut;
            const isInRange = checkIn && checkOut && dateStr > checkIn && dateStr < checkOut;

            let bgClass = "bg-white hover:bg-gray-100 text-gray-900";
            if (isPast) bgClass = "bg-gray-50 text-gray-300 cursor-not-allowed";
            else if (!isAvailable) bgClass = "bg-gray-50 text-gray-300 line-through cursor-not-allowed decoration-gray-300";
            else if (isSelected) bgClass = "bg-brand text-white hover:bg-brand shadow-md transform scale-105 rounded-lg";
            else if (isInRange) bgClass = "bg-brand/10 text-brand font-bold rounded-none";

            days.push(
                <button
                    key={dateStr}
                    disabled={isPast || (!isAvailable && !calendarLoading)}
                    onClick={() => handleDateSelect(dateStr, isAvailable)}
                    className={`h-10 md:h-12 w-full flex flex-col items-center justify-center transition-all duration-200 text-sm ${bgClass}`}
                >
                    <span>{d}</span>
                    {data?.price && isAvailable && !isSelected && !isInRange && (
                        <span className="text-[9px] text-gray-500 font-normal hidden md:block">$ {Math.round(data.price)}</span>
                    )}
                </button>
            );
        }
        return days;
    };

    // --- QUOTE & BOOKING ---
    const handleCalculateQuote = async () => {
        if (!id || !checkIn || !checkOut) return;
        setQuoteLoading(true);
        setError(null);
        setQuote(null);
        try {
            const data = await getQuote(id, checkIn, checkOut, guests, coupon);
            if (data.available) setQuote(data);
            else setError(data.message || "Dates not available.");
        } catch (err) { setError("Error calculating quote."); }
        finally { setQuoteLoading(false); }
    };

    const handleOpenBookingModal = () => {
        if (!id || !checkIn || !checkOut || !quote) return;
        setIsBookingModalOpen(true);
    };

    const scrollToBooking = () => {
        if (bookingSectionRef.current) {
            bookingSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // --- GALLERY LOGIC ---
    const openGallery = (index: number) => {
        setCurrentPhotoIndex(index);
        setIsGalleryOpen(true);
        setIsImageLoading(true);
        document.body.style.overflow = 'hidden';
    };

    const closeGallery = () => {
        setIsGalleryOpen(false);
        document.body.style.overflow = 'unset';
    };

    const nextPhoto = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (!property) return;
        const allImages = property.images || [property.image];
        setIsImageLoading(true);
        setCurrentPhotoIndex((prev) => (prev + 1) % allImages.length);
    };

    const prevPhoto = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        if (!property) return;
        const allImages = property.images || [property.image];
        setIsImageLoading(true);
        setCurrentPhotoIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
    };

    const toggleAmenitiesModal = (isOpen: boolean) => {
        setIsAmenitiesModalOpen(isOpen);
        document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    };

    const toggleReviewsModal = (isOpen: boolean) => {
        setIsReviewsModalOpen(isOpen);
        document.body.style.overflow = isOpen ? 'hidden' : 'unset';
    };

    const handleBack = () => {
        const params: any = {};
        if (checkIn) params.checkIn = checkIn;
        if (checkOut) params.checkOut = checkOut;
        if (guests) params.guests = guests.toString();
        navigate({ pathname: '/villas', search: createSearchParams(params).toString() });
    };

    if (loading) return <div className="flex justify-center items-center h-screen"><Loader2 className="w-10 h-10 text-brand animate-spin"/></div>;
    if (!property) return <div className="flex justify-center items-center h-screen">Property not found</div>;

    const allImages = property.images && property.images.length > 0 ? property.images : [property.image];
    const mainImage = allImages[0];
    const subImages = allImages.slice(1, 5);
    const remainingImagesCount = Math.max(0, allImages.length - 5);
    const AMENITIES_LIMIT = 9;
    const displayedAmenities = property.amenities?.slice(0, AMENITIES_LIMIT);
    const remainingAmenitiesCount = (property.amenities?.length || 0) - AMENITIES_LIMIT;

    // --- DESCRIPTION PARSING LOGIC ---
    const rawDescription = property.description;
    let fullDescriptionText = "";
    let descriptionSections: { title: string; content: string }[] = [];
    let isDescriptionObject = false;

    if (typeof rawDescription === 'string') {
        fullDescriptionText = rawDescription;
    } else if (typeof rawDescription === 'object' && rawDescription !== null) {
        isDescriptionObject = true;
        descriptionSections = Object.entries(rawDescription).map(([key, value]) => ({
            title: key,
            content: value
        }));
        fullDescriptionText = descriptionSections.map(s => s.content).join('\n\n');
    } else {
        fullDescriptionText = "No description available.";
    }

    const MAX_DESC_LENGTH = 350;
    const isLongDescription = fullDescriptionText.length > MAX_DESC_LENGTH;

    const renderDescriptionContent = () => {
        if (isDescriptionExpanded && isDescriptionObject) {
            return (
                <div className="space-y-1 animate-fade-in border-t border-gray-200 mt-4">
                    {descriptionSections.map((section, idx) => (
                        <DescriptionSectionItem
                            key={idx}
                            title={section.title}
                            content={section.content}
                            initialOpen={idx === 0}
                        />
                    ))}
                </div>
            );
        }

        const textToDisplay = (isDescriptionExpanded || !isLongDescription)
            ? fullDescriptionText
            : fullDescriptionText.slice(0, MAX_DESC_LENGTH) + "...";

        return <p className="whitespace-pre-line text-gray-600 leading-relaxed">{textToDisplay}</p>;
    };

    // --- CALCOLO MEDIA RECENSIONI REALI ---
    let displayRating = property.rating;
    let reviewCount = reviews.length;

    if (reviewCount > 0) {
        const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
        displayRating = parseFloat((sum / reviewCount).toFixed(1));
    }

    return (
        <div className="bg-gray-50 min-h-screen pb-24 pt-20 md:pt-28 font-sans">

            {/* --- BACK BUTTON --- */}
            <div className="max-w-7xl mx-auto px-4 md:px-8 mb-6">
                <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-gray-500 hover:text-brand font-bold transition-colors text-sm md:text-base"
                >
                    <ArrowLeft className="w-4 h-4 md:w-5 md:h-5" /> Back to Properties
                </button>
            </div>

            {/* --- MODALS --- */}
            {quote && id && (
                <BookingPaymentModal isOpen={isBookingModalOpen} onClose={() => setIsBookingModalOpen(false)} listingId={id} checkIn={checkIn} checkOut={checkOut} guests={guests} totalPrice={quote.totalPrice} currency={quote.currency} coupon={coupon} tag={property.tag || ""} />
            )}

            {isCalendarOpen && (
                <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in" onClick={() => setIsCalendarOpen(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 shadow-2xl relative" onClick={e => e.stopPropagation()}>
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-xl font-bold">Select Dates</h3>
                            <button onClick={() => setIsCalendarOpen(false)} className="p-2 bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
                        </div>
                        <div className="flex justify-between items-center mb-4">
                            <button onClick={() => handleMonthChange(-1)}><ChevronLeft className="w-6 h-6" /></button>
                            <span className="font-bold">{calendarViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                            <button onClick={() => handleMonthChange(1)}><ChevronRight className="w-6 h-6" /></button>
                        </div>
                        <div className="grid grid-cols-7 gap-1">
                            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d} className="text-center text-xs font-bold text-gray-400 py-2">{d}</div>)}
                            {renderCalendarGrid()}
                        </div>
                        <div className="mt-6 pt-4 border-t flex justify-between">
                            <button onClick={() => { setCheckIn(''); setCheckOut(''); }} className="text-sm underline font-bold text-gray-500">Clear</button>
                            <button onClick={() => setIsCalendarOpen(false)} className="bg-black text-white px-6 py-2 rounded-lg font-bold">Done</button>
                        </div>
                    </div>
                </div>
            )}

            {isAmenitiesModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4 backdrop-blur-sm" onClick={() => toggleAmenitiesModal(false)}>
                    <div className="bg-white rounded-t-3xl md:rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl animate-slide-up md:animate-zoom-in" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b flex justify-between items-center">
                            <h3 className="text-xl font-bold">Amenities</h3>
                            <button onClick={() => toggleAmenitiesModal(false)}><X className="w-6 h-6" /></button>
                        </div>
                        <div className="p-6 overflow-y-auto">
                            {property.amenities?.map((a, i) => (
                                <div key={i} className="flex items-center py-3 border-b last:border-0 border-gray-100">
                                    <CheckCircle className="w-5 h-5 text-brand/50 mr-3 flex-shrink-0" /> <span className="text-gray-700">{a}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* --- REVIEWS MODAL --- */}
            {isReviewsModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4 backdrop-blur-sm" onClick={() => toggleReviewsModal(false)}>
                    <div className="bg-white rounded-t-3xl md:rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl animate-slide-up md:animate-zoom-in" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10 rounded-t-3xl md:rounded-t-2xl">
                            <h3 className="text-xl font-bold flex items-center gap-2">
                                <Star className="w-5 h-5 text-brand fill-current" />
                                {displayRating} · {reviewCount} Reviews
                            </h3>
                            <button onClick={() => toggleReviewsModal(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><X className="w-6 h-6" /></button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-8">
                            {reviews.map((review) => (
                                <div key={review.id} className="border-b border-gray-100 pb-6 last:border-0">
                                    <div className="flex items-center mb-3">
                                        <div className="w-12 h-12 bg-brand/10 text-brand rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                                            <User className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-900">{review.authorName}</h4>
                                            <div className="flex items-center text-xs text-gray-500 mt-0.5">
                                                <span>{new Date(review.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</span>
                                                <span className="mx-2">•</span>
                                                <div className="flex items-center"><Star className="w-3 h-3 text-brand fill-current mr-1" />{review.rating}</div>
                                                {review.channel && <><span className="mx-2">•</span><span>via {review.channel}</span></>}
                                            </div>
                                        </div>
                                    </div>
                                    <p className="text-gray-700 leading-relaxed whitespace-pre-line text-sm md:text-base">
                                        {review.content}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* --- GALLERY LIGHTBOX --- */}
            {isGalleryOpen && (
                <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-md flex items-center justify-center p-0 md:p-4 animate-fade-in" onClick={closeGallery}>
                    <button onClick={closeGallery} className="absolute top-4 right-4 md:top-6 md:right-6 text-white bg-white/10 p-3 rounded-full z-[110] hover:bg-white/20 transition-all"><X className="w-6 h-6 md:w-8 md:h-8" /></button>
                    <button onClick={prevPhoto} className="absolute left-2 md:left-8 text-white bg-white/10 p-3 md:p-4 rounded-full z-[110] hover:bg-white/20 transition-all"><ChevronLeft className="w-6 h-6 md:w-8 md:h-8" /></button>
                    <div className="w-full h-full flex items-center justify-center p-2" onClick={(e) => e.stopPropagation()}>
                        {isImageLoading && <Loader2 className="w-12 h-12 text-white animate-spin absolute" />}
                        <img src={allImages[currentPhotoIndex]} alt="Gallery" onLoad={() => setIsImageLoading(false)} className={`max-h-[90vh] max-w-full object-contain rounded-lg shadow-2xl transition-opacity duration-300 ${isImageLoading ? 'opacity-0' : 'opacity-100'}`} />
                    </div>
                    <button onClick={nextPhoto} className="absolute right-2 md:right-8 text-white bg-white/10 p-3 md:p-4 rounded-full z-[110] hover:bg-white/20 transition-all"><ChevronRight className="w-6 h-6 md:w-8 md:h-8" /></button>
                    <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 font-bold tracking-widest text-sm">{currentPhotoIndex + 1} / {allImages.length}</div>
                </div>
            )}

            {/* --- MAIN CONTENT --- */}
            <div className="max-w-7xl mx-auto px-4 md:px-8">

                {/* Hero Images Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 h-[300px] md:h-[500px] rounded-2xl md:rounded-[2rem] overflow-hidden shadow-md">
                    <div className="h-full relative group cursor-pointer" onClick={() => openGallery(0)}>
                        <img src={mainImage} alt={property.title} className="w-full h-full object-cover transition duration-700" />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition duration-300"></div>
                        <div className="absolute bottom-4 right-4 md:hidden bg-white/90 px-3 py-1 rounded-lg text-xs font-bold shadow-sm flex items-center gap-1">
                            <Tag className="w-3 h-3"/> {allImages.length} Photos
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 md:gap-4 h-full hidden md:grid">
                        {subImages.map((img, idx) => (
                            <div key={idx} className="relative group cursor-pointer overflow-hidden h-full" onClick={() => openGallery(idx + 1)}>
                                <img src={img} alt={`Detail ${idx}`} className="w-full h-full object-cover transition duration-500 group-hover:scale-105" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition duration-300"></div>
                                {idx === 3 && remainingImagesCount > 0 && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><span className="text-white text-xl font-bold">+{remainingImagesCount}</span></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 mt-8 lg:mt-12">
                    {/* LEFT COLUMN: INFO */}
                    <div className="lg:col-span-2 space-y-8 lg:space-y-12">
                        {/* Header Info */}
                        <div>
                            <div className="flex flex-wrap items-center gap-2 mb-3">
                                {property.tag && <span className="bg-brand/10 text-brand px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase">{property.tag === "Direct Booking" ? "Direct Booking" : "Owner Approval"}</span>}
                                {reviewCount > 0 && (
                                    <div className="flex items-center text-sm font-medium text-gray-900 cursor-pointer hover:underline" onClick={() => toggleReviewsModal(true)}>
                                        <Star className="w-4 h-4 text-brand mr-1 fill-current" />
                                        <b>{displayRating}</b>
                                        <span className="text-gray-500 ml-1">({reviewCount} reviews)</span>
                                    </div>
                                )}
                            </div>
                            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-3 font-serif leading-tight">{property.title}</h1>
                            <div className="flex items-center text-gray-500 text-base md:text-lg mb-6"><MapPin className="w-5 h-5 mr-2 text-brand" /> {property.location}</div>

                            <div className="flex flex-wrap gap-4 md:gap-8 border-y border-gray-100 py-6 md:py-8">
                                <div className="flex items-center text-gray-700 text-sm md:text-base"><User className="w-5 h-5 mr-2 md:mr-3 text-gray-400" /> <b>{property.guests}</b> Guests</div>
                                <div className="flex items-center text-gray-700 text-sm md:text-base"><DoorOpen className="w-5 h-5 mr-2 md:mr-3 text-gray-400" /> <b>{property.bedrooms}</b> Bedrooms</div>
                                <div className="flex items-center text-gray-700 text-sm md:text-base"><Bed className="w-5 h-5 mr-2 md:mr-3 text-gray-400" /> <b>{property.beds}</b> Beds</div>
                                <div className="flex items-center text-gray-700 text-sm md:text-base"><Bath className="w-5 h-5 mr-2 md:mr-3 text-gray-400" /> <b>{property.baths}</b> Baths</div>
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 font-serif">Description</h2>

                            <div className="prose prose-sm md:prose-lg">
                                {renderDescriptionContent()}
                            </div>

                            {isLongDescription && (
                                <button
                                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                    className="mt-4 text-brand font-bold text-sm flex items-center gap-1 hover:underline outline-none"
                                >
                                    {isDescriptionExpanded ? "Show less" : "Read more"}
                                    {isDescriptionExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                </button>
                            )}
                        </div>

                        {/* Amenities */}
                        <div>
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 font-serif">What this place offers</h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                                {displayedAmenities?.map((amenity, index) => (
                                    <div key={index} className="flex items-center p-3 md:p-4 bg-gray-50 rounded-xl">
                                        <CheckCircle className="w-5 h-5 text-brand/50 mr-3 flex-shrink-0" />
                                        <span className="text-gray-700 font-medium truncate text-sm md:text-base">{amenity}</span>
                                    </div>
                                ))}
                            </div>
                            {remainingAmenitiesCount > 0 && (
                                <button onClick={() => toggleAmenitiesModal(true)} className="mt-6 w-full md:w-auto px-6 py-3 border-2 border-gray-900 rounded-xl text-gray-900 font-bold hover:bg-gray-900 hover:text-white transition">
                                    Show all {property.amenities?.length} amenities
                                </button>
                            )}
                        </div>

                        {/* Reviews Section */}
                        {reviews.length > 0 && (
                            <div className="pt-8 border-t border-gray-100">
                                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 font-serif flex items-center gap-2">
                                    <Star className="w-6 h-6 text-brand fill-current" /> {displayRating} · {reviewCount} Reviews
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {reviews.slice(0, 4).map((review) => (
                                        <div key={review.id} className="bg-gray-50 p-6 rounded-2xl">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 bg-brand/10 text-brand rounded-full flex items-center justify-center mr-3 flex-shrink-0"><User className="w-5 h-5" /></div>
                                                    <div>
                                                        <h4 className="font-bold text-gray-900 text-sm">{review.authorName}</h4>
                                                        <span className="text-xs text-gray-500">
                                                            {new Date(review.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                                                            {review.channel && ` · via ${review.channel}`}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center text-xs font-bold text-gray-900"><Star className="w-3.5 h-3.5 text-brand fill-current mr-1" />{review.rating}</div>
                                            </div>
                                            <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">"{review.content}"</p>
                                        </div>
                                    ))}
                                </div>
                                {reviewCount > 4 && (
                                    <button
                                        onClick={() => toggleReviewsModal(true)}
                                        className="mt-6 w-full md:w-auto px-6 py-3 border-2 border-gray-900 rounded-xl text-gray-900 font-bold hover:bg-gray-900 hover:text-white transition flex items-center justify-center gap-2"
                                    >
                                        <MessageSquare className="w-4 h-4" /> Show all {reviewCount} reviews
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Map */}
                        <div className="pt-8 border-t border-gray-100 mt-8">
                            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 font-serif">Where you'll be</h2>
                            <div className="w-full h-[300px] md:h-[400px] bg-gray-100 rounded-3xl overflow-hidden shadow-sm relative z-0">
                                {property.lat && property.lng ? (
                                    <MapContainer center={[property.lat, property.lng]} zoom={14} scrollWheelZoom={false} className="w-full h-full z-0" style={{ height: '100%', width: '100%' }}>
                                        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>' url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY ?? ''}`} />
                                        <Circle center={[property.lat, property.lng]} radius={500} pathOptions={{ color: '#ea580c', fillColor: '#ea580c', fillOpacity: 0.2, weight: 1 }} />
                                        <MapUpdater center={[property.lat, property.lng]} />
                                    </MapContainer>
                                ) : (
                                    <iframe width="100%" height="100%" src={`https://maps.google.com/maps?q=${encodeURIComponent(property.location)}&t=&z=12&ie=UTF8&iwloc=&output=embed`} title="Loc" className="w-full h-full filter grayscale-[20%]" ></iframe>
                                )}
                                <div className="absolute bottom-4 left-4 z-[400] bg-white/90 backdrop-blur-md px-4 py-2 rounded-xl text-sm font-bold shadow-lg flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-brand" /> {property.location}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: BOOKING CARD */}
                    <div className="hidden lg:block lg:col-span-1" ref={bookingSectionRef}>
                        <div className="sticky top-28 bg-white p-8 rounded-[2rem] shadow-2xl border border-gray-100">
                            <div className="flex justify-between items-end mb-8">
                                <div>
                                    <span className="text-3xl font-bold text-gray-900">{quote ? `${quote.currency} ${quote.nightlyPrice?.toFixed(0)}` : `$ ${property.price}`}</span>
                                    <span className="text-gray-500"> / night</span>
                                </div>
                                {reviewCount > 0 && (
                                    <div className="flex items-center text-sm font-medium text-gray-500">
                                        <Star className="w-4 h-4 text-brand mr-1 fill-current" /> {displayRating}
                                    </div>
                                )}
                            </div>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div onClick={() => openCalendarModal('checkIn')} className="border border-gray-200 rounded-xl p-3 cursor-pointer hover:border-brand bg-gray-50">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Check-in</label>
                                        <div className="text-sm font-bold truncate">{checkIn || 'Add date'}</div>
                                    </div>
                                    <div onClick={() => openCalendarModal('checkOut')} className="border border-gray-200 rounded-xl p-3 cursor-pointer hover:border-brand bg-gray-50">
                                        <label className="text-[10px] font-bold text-gray-400 uppercase">Check-out</label>
                                        <div className="text-sm font-bold truncate">{checkOut || 'Add date'}</div>
                                    </div>
                                </div>
                                <div className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase">Guests</label>
                                    <select value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="w-full bg-transparent font-bold text-sm outline-none cursor-pointer">
                                        {[...Array(property.guests)].map((_, i) => <option key={i} value={i + 1}>{i + 1} Guests</option>)}
                                    </select>
                                </div>
                                <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-gray-400" />
                                    <input type="text" placeholder="Promo Code" className="bg-transparent text-sm font-bold w-full outline-none" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
                                </div>
                                <button onClick={handleCalculateQuote} disabled={!checkIn || !checkOut || quoteLoading} className="w-full bg-white border-2 border-brand text-brand py-3 rounded-xl font-bold hover:bg-brand hover:text-white transition">
                                    {quoteLoading ? <Loader2 className="w-5 h-5 mx-auto animate-spin" /> : 'Check Availability'}
                                </button>
                                {error && <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm flex items-center"><AlertCircle className="w-4 h-4 mr-2" /> {error}</div>}

                                {/* DETTAGLIO PREZZO (USO COMPONENTE CUSTOM) */}
                                {quote && <PriceBreakdown quote={quote} coupon={coupon} />}

                                <button onClick={handleOpenBookingModal} disabled={!quote} className={`w-full py-4 rounded-xl font-bold text-lg shadow-xl flex items-center justify-center gap-2 transition-all ${quote ? 'bg-brand text-white hover:bg-gray-900 transform hover:-translate-y-1' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                                    Proceed to Booking <ChevronRight className="w-5 h-5" />
                                </button>

                                {/* --- RESERVATION NOTICE (DESKTOP) --- */}
                                <div className="mt-3 text-[10px] text-gray-400 leading-tight text-center">
                                    <span className="font-bold">Reservation Notice:</span> All reservations are subject to review and confirmation. Availability, length of stay, and acceptance may vary based on property‑specific and local regulatory requirements.
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* MOBILE BOOKING */}
                    <div className="lg:hidden" ref={bookingSectionRef}>
                        <div className="bg-white p-6 rounded-3xl shadow-lg border border-gray-100">
                            <h3 className="text-xl font-bold mb-4">Book your stay</h3>
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div onClick={() => openCalendarModal('checkIn')} className="border border-gray-200 rounded-xl p-3 bg-gray-50"><label className="text-[10px] font-bold text-gray-400 uppercase">Check-in</label><div className="text-sm font-bold">{checkIn || 'Add date'}</div></div>
                                    <div onClick={() => openCalendarModal('checkOut')} className="border border-gray-200 rounded-xl p-3 bg-gray-50"><label className="text-[10px] font-bold text-gray-400 uppercase">Check-out</label><div className="text-sm font-bold">{checkOut || 'Add date'}</div></div>
                                </div>
                                <div className="border border-gray-200 rounded-xl p-3 bg-gray-50"><label className="text-[10px] font-bold text-gray-400 uppercase">Guests</label><select value={guests} onChange={(e) => setGuests(Number(e.target.value))} className="w-full bg-transparent font-bold text-sm outline-none">{[...Array(property.guests)].map((_, i) => <option key={i} value={i + 1}>{i + 1} Guests</option>)}</select></div>
                                <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 flex items-center gap-2">
                                    <Tag className="w-4 h-4 text-gray-400" />
                                    <input type="text" placeholder="Promo Code" className="bg-transparent text-sm font-bold w-full outline-none" value={coupon} onChange={(e) => setCoupon(e.target.value)} />
                                </div>
                                <button onClick={handleCalculateQuote} disabled={!checkIn || !checkOut || quoteLoading} className="w-full bg-white border-2 border-brand text-brand py-3 rounded-xl font-bold">{quoteLoading ? <Loader2 className="w-5 h-5 mx-auto animate-spin" /> : 'Check Availability'}</button>
                                {error && <div className="p-3 bg-red-50 text-red-600 rounded-xl text-sm">{error}</div>}

                                {/* DETTAGLIO PREZZO MOBILE (USO COMPONENTE CUSTOM) */}
                                {quote && <PriceBreakdown quote={quote} coupon={coupon} />}

                                <button onClick={handleOpenBookingModal} disabled={!quote} className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg ${quote ? 'bg-brand text-white' : 'bg-gray-200 text-gray-400'}`}>Proceed to Booking</button>

                                {/* --- RESERVATION NOTICE (MOBILE) --- */}
                                <div className="mt-3 text-[10px] text-gray-400 leading-tight text-center">
                                    <span className="font-bold">Reservation Notice:</span> All reservations are subject to review and confirmation. Availability, length of stay, and acceptance may vary based on property‑specific and local regulatory requirements.
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* --- MOBILE STICKY BAR --- */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 pb-6 z-40 lg:hidden flex justify-between items-center shadow-[0_-5px_20px_rgba(0,0,0,0.05)]">
                <div>
                    <div className="text-lg font-bold text-gray-900">{quote ? `${quote.currency} ${quote.totalPrice?.toFixed(0)}` : `$ ${property.price}`}</div>
                    <div className="text-xs text-gray-500 font-medium">{quote ? 'Total for stay' : '/ night'}</div>
                </div>
                <button onClick={scrollToBooking} className="bg-brand text-white px-6 py-3 rounded-xl font-bold text-sm shadow-md active:scale-95 transition-transform">{quote ? 'Continue' : 'Check Availability'}</button>
            </div>

        </div>
    );
};

export default PropertyDetail;